import { validateValue } from '@vft/core/validation';
import { cleanWhitespace } from './whitespaceCleanup.js';
import { toProperCase } from './properCase.js';
import { normalizeDate } from './dateNormalizer.js';
import { findClosestDomain } from './domainMatcher.js';
import { proposeRelocate } from './relocate.js';
import { STRATEGIES, scopeForStrategy } from './strategies.js';

export { STRATEGIES } from './strategies.js';

function getFieldId(field) {
  return field.id || field.name || field.label;
}

// `scope` selecciona quines estratègies es generen:
//   - 'generic' → correccions generals (l'únic pas que fa servir l'app)
//   - 'all'     → totes (per defecte; usat als tests i al càlcul d'un sol pas)
// Les correccions lligades a un camp concret ja no viuen aquí: són programes
// del model que corren al pas 7 (Càlculs).
function applyStrategies(value, field, scope = 'all') {
  const candidates = [];
  const allowGeneric = scope !== 'field';
  const str = typeof value === 'string' ? value : value instanceof Date ? null : String(value ?? '');

  if (allowGeneric && typeof str === 'string') {
    const ws = cleanWhitespace(str);
    if (ws != null) candidates.push({ strategy: STRATEGIES.WHITESPACE, value: ws });
  }
  if (allowGeneric && typeof str === 'string') {
    const pc = toProperCase(str);
    if (pc != null) candidates.push({ strategy: STRATEGIES.PROPER_CASE, value: pc });
  }
  if (allowGeneric) {
    const datePattern = field?.validation?.format?.datePattern;
    if (datePattern) {
      const nd = normalizeDate(value, datePattern);
      if (nd != null) candidates.push({ strategy: STRATEGIES.DATE, value: nd });
    }
    const domain = field?.validation?.domain;
    if (Array.isArray(domain) && domain.length > 0) {
      const match = findClosestDomain(value, domain);
      if (match) candidates.push({ strategy: STRATEGIES.DOMAIN, value: match.key });
    }
  }

  return candidates;
}

function isEnabled(enabled, strategy) {
  if (!enabled) return true;
  return enabled[strategy] !== false;
}

export function proposeCorrection(value, field, options = {}) {
  if (value === null || value === undefined) return null;
  const scope = options.scope || 'all';
  const current = validateValue(value, field);
  if (current.valid) return null;
  for (const candidate of applyStrategies(value, field, scope)) {
    if (!isEnabled(options.enabled, candidate.strategy)) continue;
    const r = validateValue(candidate.value, field);
    if (r.valid) return candidate;
  }
  // `relocate` és una correcció general (pas 1): mou valors mal posats a la seva
  // columna. No s'aplica al pas de camp concret.
  if (scope !== 'field' && isEnabled(options.enabled, STRATEGIES.RELOCATE) && options.otherFields && options.rowSnapshot) {
    const fieldWithIndex = { ...field, _index: options.currentFieldIndex };
    const relocate = proposeRelocate(value, fieldWithIndex, options.otherFields, options.rowSnapshot);
    if (relocate) {
      return { strategy: STRATEGIES.RELOCATE, ...relocate };
    }
  }
  return null;
}

export function mergeMutualSwaps(proposals) {
  const flagsByRow = new Map();
  proposals.forEach((p, idx) => {
    if (p.strategy === STRATEGIES.RELOCATE && p.kind === 'flag') {
      if (!flagsByRow.has(p.rowIndex)) flagsByRow.set(p.rowIndex, []);
      flagsByRow.get(p.rowIndex).push({ idx, p });
    }
  });

  const toRemove = new Set();
  const swapPatch = new Map();

  for (const entries of flagsByRow.values()) {
    for (let i = 0; i < entries.length; i += 1) {
      if (toRemove.has(entries[i].idx) || swapPatch.has(entries[i].idx)) continue;
      for (let j = i + 1; j < entries.length; j += 1) {
        if (toRemove.has(entries[j].idx) || swapPatch.has(entries[j].idx)) continue;
        const a = entries[i].p;
        const b = entries[j].p;
        if (a.targetFieldIndex === b.fieldIndex && b.targetFieldIndex === a.fieldIndex) {
          const lower = a.fieldIndex <= b.fieldIndex ? entries[i] : entries[j];
          const higher = a.fieldIndex <= b.fieldIndex ? entries[j] : entries[i];
          swapPatch.set(lower.idx, higher.p);
          toRemove.add(higher.idx);
          break;
        }
      }
    }
  }

  return proposals
    .map((p, idx) => {
      if (!swapPatch.has(idx)) return p;
      const mate = swapPatch.get(idx);
      return {
        ...p,
        kind: 'swap',
        swapWithFieldId: mate.fieldId,
        swapWithFieldIndex: mate.fieldIndex,
        swapWithValue: mate.originalValue,
        targetCurrentValue: null,
      };
    })
    .filter((_, idx) => !toRemove.has(idx));
}

export function proposeCorrectionsForOutput(outputRows, fields, options = {}) {
  const scope = options.scope || 'all';
  const proposals = [];
  const fieldArr = fields.map((f, idx) => ({ field: f, id: getFieldId(f), index: idx }));
  outputRows.rows.forEach((row, rowIndex) => {
    fieldArr.forEach(({ field, id, index }) => {
      // Ja no hi ha estratègies de camp concret (l'email és un programa del pas 7).
      if (scope === 'field') return;
      const value = row[index];
      const otherFields = fieldArr
        .filter((o) => o.index !== index)
        .map((o) => ({ field: o.field, fieldIndex: o.index }));
      const proposal = proposeCorrection(value, field, {
        ...options,
        scope,
        otherFields,
        rowSnapshot: row,
        currentFieldIndex: index,
      });
      if (proposal) {
        proposals.push({
          rowIndex,
          fieldId: id,
          fieldIndex: index,
          originalValue: value,
          proposedValue: proposal.value ?? null,
          strategy: proposal.strategy,
          scope: scopeForStrategy(proposal.strategy),
          kind: proposal.kind || null,
          targetFieldId: proposal.targetFieldId || null,
          targetFieldIndex: proposal.targetFieldIndex ?? null,
          targetCurrentValue: proposal.targetCurrentValue ?? null,
        });
      }
    });
  });
  return mergeMutualSwaps(proposals);
}

export function applyCorrections(outputRows, proposals, rejected = {}) {
  const rows = outputRows.rows.map((r) => [...r]);
  for (const p of proposals) {
    const key = `${p.rowIndex}:${p.fieldId}`;
    if (rejected[key]) continue;
    if (p.strategy === STRATEGIES.RELOCATE) {
      if (p.kind === 'move' && p.targetFieldIndex != null) {
        rows[p.rowIndex][p.fieldIndex] = null;
        rows[p.rowIndex][p.targetFieldIndex] = p.originalValue;
      } else if (p.kind === 'swap' && p.swapWithFieldIndex != null) {
        const tmp = rows[p.rowIndex][p.fieldIndex];
        rows[p.rowIndex][p.fieldIndex] = rows[p.rowIndex][p.swapWithFieldIndex];
        rows[p.rowIndex][p.swapWithFieldIndex] = tmp;
      }
      continue;
    }
    rows[p.rowIndex][p.fieldIndex] = p.proposedValue;
  }
  return { headers: outputRows.headers, rows };
}
